import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Sparkles, Send, Check, Loader2, X, Users, Zap, BarChart3, Eye, Mail, CalendarClock } from 'lucide-react';
import { insertBookingToken } from '@/lib/bookingToken';
import { EmailImagePanel } from './EmailImagePanel';
import { LintBadge } from './LintBadge';
import CampaignManager from './CampaignManager';
import PersonalLetters from './PersonalLetters';
import WeeklyFitterComposer from './WeeklyFitterComposer';
import FittersList from './FittersList';
import { statusLabel, NOTICE_STYLE } from './IssueNotices';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

const EMAIL_TYPES = ['Announcement', 'Follow-Up', 'Review Request', 'Nurture'];

function AudienceOverview() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/email/audiences`);
        setGroups(res.data.groups);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-sm text-hg-text-dim py-4">Loading audiences...</div>;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {groups.map((g, i) => (
        <div key={i} data-testid={`audience-card-${i}`} className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-hg-green" strokeWidth={1.5} />
            <span className="text-xs font-sans font-medium text-hg-text">{g.name}</span>
          </div>
          <div className="text-2xl font-display text-hg-text">{g.subscriber_count.toLocaleString()}</div>
          <p className="text-xs text-hg-text-dim font-sans">{g.description}</p>
        </div>
      ))}
    </div>
  );
}

function AutomationStatus() {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/email/automations`);
        setAutomations(res.data.automations);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const toggleAutomation = async (name, currentActive) => {
    try {
      await axios.post(`${API}/email/toggle-automation?name=${encodeURIComponent(name)}&active=${!currentActive}`);
      setAutomations(automations.map(a => a.name === name ? { ...a, active: !a.active } : a));
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="text-sm text-hg-text-dim py-4">Loading...</div>;

  return (
    <div className="space-y-2">
      {automations.map((a, i) => (
        <div key={i} data-testid={`automation-${i}`} className="bg-hg-surface border border-hg-border rounded-sm p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap size={14} className={a.active ? 'text-hg-green' : 'text-hg-text-dim'} strokeWidth={1.5} />
            <div>
              <div className="text-sm font-sans text-hg-text">{a.name}</div>
              <div className="text-xs text-hg-text-dim font-sans">{a.target}</div>
            </div>
          </div>
          <button
            data-testid={`automation-toggle-${i}`}
            onClick={() => toggleAutomation(a.name, a.active)}
            className={`relative w-10 h-5 rounded-full transition-colors ${a.active ? 'bg-hg-green' : 'bg-hg-border'}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${a.active ? 'left-5' : 'left-0.5'}`} />
          </button>
        </div>
      ))}
    </div>
  );
}

function CampaignHistory() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsById, setStatsById] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [hist, analytics] = await Promise.all([
          axios.get(`${API}/email/history`),
          axios.get(`${API}/mailerlite/analytics?status=all&limit=50`).catch(() => ({ data: { campaigns: [] } })),
        ]);
        if (cancelled) return;
        setEmails(hist.data.emails);
        const map = {};
        for (const c of (analytics.data?.campaigns || [])) {
          map[c.campaign_id] = c.stats;
        }
        setStatsById(map);
      } catch (e) { console.error(e); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-3">
      {loading && <div className="text-center py-8 text-hg-text-dim text-sm">Loading...</div>}
      {!loading && emails.length === 0 && (
        <div className="text-center py-12 text-hg-text-dim text-sm">No email campaigns yet.</div>
      )}
      {emails.map((em, i) => {
        const stats = em.mailerlite_campaign_id ? statsById[em.mailerlite_campaign_id] : null;
        const opens = stats?.opens_count || 0;
        const openRate = stats?.open_rate || 0;
        const clicks = stats?.clicks_count || 0;
        return (
          <div key={em.id || i} data-testid={`email-history-row-${i}`} className="bg-hg-surface border border-hg-border rounded-sm p-4">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="text-xs text-hg-text-dim font-sans">
                {new Date(em.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              <span className="text-xs font-sans text-hg-green">{em.email_type}</span>
              {em.planned_for && (
                <span data-testid={`email-planned-for-${i}`} className="text-xs font-sans text-hg-text-dim">
                  planned for {new Date(`${em.planned_for}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
              <span className="text-xs font-sans text-hg-text-dim">{em.audience}</span>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-sans ${
                em.status === 'pushed' ? 'bg-hg-green/10 text-hg-green' : 'bg-hg-border text-hg-text-dim'
              }`}>{statusLabel(em)}</span>
              {(em.skipped || em.held_reason) && (
                <span data-testid={`email-state-note-${i}`} className="text-[10px] px-2 py-0.5 rounded-sm font-sans"
                      style={{ background: NOTICE_STYLE[em.skipped ? 'skipped' : 'held'].bg,
                               color: NOTICE_STYLE[em.skipped ? 'skipped' : 'held'].fg }}>
                  {em.skipped ? em.skipped.reason : `until ${em.held_until || 'the hold lifts'} — ${em.held_reason}`}
                </span>
              )}
              {stats && opens > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-sm font-sans bg-hg-green/10 text-hg-green flex items-center gap-1" data-testid={`email-stats-${i}`}>
                  <BarChart3 size={10} /> {opens} opens · {Math.round(openRate * 100) / 100}%{clicks > 0 ? ` · ${clicks} clicks` : ''}
                </span>
              )}
            </div>
            <p className="text-sm font-sans text-hg-text">{em.subject}</p>
          </div>
        );
      })}
    </div>
  );
}

function EmailFormattedPreviewModal({ subject, body, audience, style, heroImage, highlightImage, ctaText, ctaUrl, onStyleChange, onClose }) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.post(`${API}/templates/preview`, {
          style,
          subject,
          headline: subject,
          body,
          eyebrow: audience || 'Henry-Griffitts',
          sign_off: '~ Randall Henry, President',
          hero_image: heroImage || null,
          highlight_image: highlightImage || null,
          cta_text: ctaText || null,
          cta_url: ctaUrl || null,
        });
        if (!cancelled) setHtml(res.data.html);
      } catch (e) { console.error(e); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [subject, body, audience, style, heroImage, highlightImage, ctaText, ctaUrl]);

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="email-formatted-preview">
      <div className="bg-white rounded-sm w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-hg-surface flex items-center justify-between px-4 py-3 border-b border-hg-border gap-3 flex-wrap">
          <span className="text-sm font-sans text-hg-text">HG Email — Formatted Preview</span>
          <div className="flex items-center gap-2">
            <div className="flex border border-hg-border rounded-sm overflow-hidden">
              {[
                { id: 'promotional', label: 'Promo' },
                { id: 'announcement', label: 'Announce' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  data-testid={`email-style-${opt.id}`}
                  onClick={() => onStyleChange(opt.id)}
                  className={`px-3 py-1 text-[11px] font-sans transition-colors ${
                    style === opt.id ? 'bg-hg-green text-hg-text' : 'bg-transparent text-hg-text-dim hover:text-hg-text'
                  }`}
                >{opt.label}</button>
              ))}
            </div>
            <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={18} /></button>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-[600px]"><Loader2 size={20} className="animate-spin text-hg-green" /></div>
        ) : (
          <iframe srcDoc={html} title="Formatted Preview" className="w-full flex-1 min-h-[600px] border-0" sandbox="allow-same-origin" />
        )}
      </div>
    </div>
  );
}

export default function EmailStudio({ onNavigate, initialTab } = {}) {
  const [emailType, setEmailType] = useState(EMAIL_TYPES[0]);
  const [audience, setAudience] = useState('Customers');
  const [brief, setBrief] = useState('');
  const [draft, setDraft] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const editBodyRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab || 'campaigns');
  const [weeklyView, setWeeklyView] = useState('composer'); // 'composer' | 'fitters'
  const [notification, setNotification] = useState('');
  const [pushing, setPushing] = useState(false);
  const [showFormatted, setShowFormatted] = useState(false);
  const [previewStyle, setPreviewStyle] = useState('promotional');
  const [heroImage, setHeroImage] = useState(null);
  const [highlightImage, setHighlightImage] = useState(null);
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');

  const audiences = ['Customers', 'Teaching Pros/Coaches', 'Dealers', 'Fitting Leads'];

  // Auto-pick template style based on email type
  useEffect(() => {
    setPreviewStyle(emailType === 'Announcement' ? 'announcement' : 'promotional');
  }, [emailType]);

  const showNotif = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(''), 3000);
  };

  const handleGenerate = async () => {
    if (!brief.trim()) return;
    setLoading(true);
    setError('');
    setDraft(null);
    try {
      const res = await axios.post(`${API}/email/generate`, {
        email_type: emailType, audience, brief,
      });
      setDraft(res.data);
      setEditSubject(res.data.subject || '');
      setEditBody(res.data.body || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate email.');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/email/save`, {
        email_type: emailType, audience,
        subject: editSubject, brief, body: editBody,
        template_style: previewStyle,
        hero_image: heroImage,
        highlight_image: highlightImage,
        cta_text: ctaText || null,
        cta_url: ctaUrl || null,
        headline: editSubject,
      });
      showNotif('Email draft saved');
    } catch (e) { console.error(e); }
  };

  const handlePushMailerlite = async () => {
    setPushing(true);
    try {
      const saveRes = await axios.post(`${API}/email/save`, {
        email_type: emailType, audience,
        subject: editSubject, brief, body: editBody,
        template_style: previewStyle,
        hero_image: heroImage,
        highlight_image: highlightImage,
        cta_text: ctaText || null,
        cta_url: ctaUrl || null,
        headline: editSubject,
      });
      const emId = saveRes.data.email?.id;
      if (emId) {
        await axios.post(`${API}/email/push-mailerlite?email_id=${emId}`);
      }
      showNotif('Campaign draft pushed to Mailerlite');
    } catch (e) { console.error(e); }
    setPushing(false);
  };

  return (
    <div className="space-y-6">
      {notification && (
        <div className="fixed top-4 right-4 z-50 bg-hg-surface border border-hg-green/30 rounded-sm px-4 py-3 text-sm text-hg-green font-sans flex items-center gap-2 animate-fade-in">
          <Check size={14} /> {notification}
        </div>
      )}

      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">EMAIL STUDIO</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">Customer, coach & dealer email campaigns and automations.</p>
      </div>

      <div className="flex gap-2 border-b border-hg-border overflow-x-auto">
        {[
          { id: 'campaigns', label: 'Campaigns' },
          { id: 'letters', label: 'Personal Letters' },
          { id: 'weekly', label: 'Weekly Fitter' },
          { id: 'compose', label: 'AI Compose' },
          { id: 'audiences', label: 'Audiences' },
          { id: 'automations', label: 'Automations' },
          { id: 'history', label: 'Campaign History' },
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`email-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-sans transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab.id ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'campaigns' && <CampaignManager onNavigate={onNavigate} />}

      {activeTab === 'letters' && <PersonalLetters />}

      {activeTab === 'weekly' && (
        <div className="space-y-4">
          <div className="inline-flex rounded-sm border border-hg-border overflow-hidden">
            {[
              { id: 'composer', label: 'Composer' },
              { id: 'fitters', label: 'Manage Fitters' },
            ].map((v) => (
              <button
                key={v.id}
                data-testid={`weekly-view-${v.id}`}
                onClick={() => setWeeklyView(v.id)}
                className={`px-4 py-1.5 text-xs font-sans transition-colors ${
                  weeklyView === v.id ? 'bg-hg-green text-hg-text' : 'bg-hg-surface text-hg-text-dim hover:text-hg-text'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          {weeklyView === 'composer' ? <WeeklyFitterComposer onNavigate={onNavigate} /> : <FittersList />}
        </div>
      )}

      {activeTab === 'compose' && (
        <div className="space-y-6">
          <div className="bg-hg-surface border border-hg-border rounded-sm p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Email Type</label>
                <select
                  data-testid="email-type-select"
                  value={emailType}
                  onChange={(e) => setEmailType(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                >
                  {EMAIL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Audience</label>
                <select
                  data-testid="email-audience-select"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                >
                  {audiences.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Brief</label>
              <textarea
                data-testid="email-brief-input"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="What's this email about..."
                className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
                rows={3}
              />
            </div>
            <button
              data-testid="generate-email-button"
              onClick={handleGenerate}
              disabled={loading || !brief.trim()}
              className="flex items-center gap-2 bg-hg-green text-hg-text font-sans font-medium text-sm px-5 py-2.5 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {loading ? 'Generating...' : 'Generate Email'}
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-sm text-red-400 font-sans flex items-center gap-2">
              <X size={14} /> {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12 gap-3">
              <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse-green" />
              <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse-green" style={{ animationDelay: '300ms' }} />
              <div className="w-2 h-2 rounded-full bg-hg-green animate-pulse-green" style={{ animationDelay: '600ms' }} />
            </div>
          )}

          {draft && (
            <div className="bg-hg-surface border border-hg-border rounded-sm p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl text-hg-text tracking-wider">EMAIL DRAFT</h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    data-testid="email-formatted-preview-btn"
                    onClick={() => setShowFormatted(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green/30 bg-hg-green/5 rounded-sm text-hg-green hover:bg-hg-green/10 transition-colors"
                  >
                    <Mail size={12} strokeWidth={1.5} /> Preview Formatted
                  </button>
                  <button
                    data-testid="email-save-btn"
                    onClick={handleSave}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green transition-colors"
                  >
                    <Check size={12} /> Save Draft
                  </button>
                  <button
                    data-testid="email-push-mailerlite-btn"
                    onClick={handlePushMailerlite}
                    disabled={pushing}
                    className="flex items-center gap-2 bg-hg-green text-hg-text text-xs font-sans font-medium px-4 py-1.5 rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
                  >
                    {pushing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Push to Mailerlite
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Subject Line</label>
                <input
                  data-testid="email-subject-edit"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
                />
              </div>
              <div>
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Email Body</label>
                <textarea
                  ref={editBodyRef}
                  data-testid="email-body-edit"
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-sm text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
                  rows={10}
                />
                <button
                  onClick={() => setEditBody(insertBookingToken(editBodyRef.current, editBody))}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-green hover:border-hg-green"
                  title="Becomes the branded Book-a-Call button when the email is pushed"
                  data-testid="email-booking-btn"
                >
                  <CalendarClock size={11} /> Insert booking link
                </button>
                {draft?.body_lint && (
                  <div className="mt-2" data-testid="email-body-lint">
                    <LintBadge lint={draft.body_lint} testid="email-body-lint-badge" />
                  </div>
                )}
              </div>

              {/* Template Style selector */}
              <div className="border-t border-hg-border pt-4">
                <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">Template Style</label>
                <div className="flex gap-2">
                  {[
                    { id: 'promotional', label: 'Promotional' },
                    { id: 'announcement', label: 'Announcement' },
                  ].map((s) => (
                    <button
                      key={s.id}
                      data-testid={`email-template-style-${s.id}`}
                      onClick={() => setPreviewStyle(s.id)}
                      className={`px-4 py-2 text-xs font-sans rounded-sm border transition-colors ${
                        previewStyle === s.id ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim hover:border-hg-green/50'
                      }`}
                    >{s.label}</button>
                  ))}
                </div>
              </div>

              {/* Hero image */}
              <EmailImagePanel
                testIdPrefix="email-hero-image"
                imageData={heroImage}
                onImageSet={setHeroImage}
                context={previewStyle === 'announcement' ? 'announcement' : 'hero'}
                promptContext={editSubject || brief}
                label="Hero Image (top of email)"
              />

              {/* Highlight image for announcement style only */}
              {previewStyle === 'announcement' && (
                <EmailImagePanel
                  testIdPrefix="email-highlight-image"
                  imageData={highlightImage}
                  onImageSet={setHighlightImage}
                  context="announcement"
                  promptContext={editSubject ? `feature graphic for ${editSubject}` : 'product or event badge'}
                  label="Highlight Image (between body & CTA)"
                  size="compact"
                />
              )}

              {/* CTA */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-hg-border pt-4">
                <div>
                  <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">CTA Button Text (optional)</label>
                  <input
                    data-testid="email-cta-text"
                    value={ctaText}
                    onChange={(e) => setCtaText(e.target.value)}
                    placeholder="Book a Fitting"
                    className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green"
                  />
                </div>
                <div>
                  <label className="block text-xs font-sans text-hg-text-dim uppercase tracking-wider mb-2">CTA Link URL (optional)</label>
                  <input
                    data-testid="email-cta-url"
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                    placeholder="https://henry-griffitts.com/fitting"
                    className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans placeholder-hg-text-dim focus:outline-none focus:ring-1 focus:ring-hg-green"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'audiences' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs font-sans text-hg-text-dim uppercase tracking-wider">
            <Users size={14} strokeWidth={1.5} /> Mailerlite Audience Groups
          </div>
          <AudienceOverview />
          <p className="text-xs text-hg-text-dim font-sans italic">Connect Mailerlite API key in Settings to see live subscriber counts.</p>
        </div>
      )}

      {activeTab === 'automations' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs font-sans text-hg-text-dim uppercase tracking-wider">
            <Zap size={14} strokeWidth={1.5} /> Active Automations
          </div>
          <AutomationStatus />
          <p className="text-xs text-hg-text-dim font-sans italic">Connect Mailerlite API key in Settings to manage live automations.</p>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs font-sans text-hg-text-dim uppercase tracking-wider">
            <BarChart3 size={14} strokeWidth={1.5} /> Campaign History
          </div>
          <CampaignHistory />
        </div>
      )}

      {showFormatted && (
        <EmailFormattedPreviewModal
          subject={editSubject}
          body={editBody}
          audience={audience}
          style={previewStyle}
          heroImage={heroImage}
          highlightImage={highlightImage}
          ctaText={ctaText}
          ctaUrl={ctaUrl}
          onStyleChange={setPreviewStyle}
          onClose={() => setShowFormatted(false)}
        />
      )}
    </div>
  );
}
